import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const TAGS_PARAM = 'tags';
const SEARCH_PARAM = 'q';

function parseTags(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
}

function serializeTags(tags: string[]): string | null {
  return tags.length > 0 ? tags.join(',') : null;
}

export function useApiExplorerSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tags = useMemo(
    () => parseTags(searchParams.get(TAGS_PARAM)),
    [searchParams],
  );

  const search = searchParams.get(SEARCH_PARAM) ?? '';

  const replaceParams = useCallback(
    (next: { tags?: string[]; search?: string }) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);

          const nextTags = next.tags ?? parseTags(params.get(TAGS_PARAM));
          const serializedTags = serializeTags(nextTags);
          if (serializedTags) params.set(TAGS_PARAM, serializedTags);
          else params.delete(TAGS_PARAM);

          const nextSearch =
            next.search !== undefined
              ? next.search
              : params.get(SEARCH_PARAM) ?? '';
          const trimmed = nextSearch.trim();
          if (trimmed) params.set(SEARCH_PARAM, trimmed);
          else params.delete(SEARCH_PARAM);

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setTags = useCallback(
    (nextTags: string[]) => {
      replaceParams({ tags: nextTags });
    },
    [replaceParams],
  );

  const setSearch = useCallback(
    (nextSearch: string) => {
      replaceParams({ search: nextSearch });
    },
    [replaceParams],
  );

  const clearAll = useCallback(() => {
    replaceParams({ tags: [], search: '' });
  }, [replaceParams]);

  return { tags, search, setTags, setSearch, clearAll };
}
